import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import WallPaperSimulatorConfigurator from './WallPaperSimulatorConfigurator';
import WallPaperSimulatorCanvas from './WallPaperSimulatorCanvas';
import ChoosenWallpaper from './ChoosenWallpaper';
import { SimulatorRoom } from '../../Types/SimulatorRoom';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import WallpaperSimulatorActionButtons from './WallpaperSimulatorActionButtons';
import FullScreenDialog from '../FullScreenDialog';
import RoomPagination from './RoomPagination';
import Icon from '../Icon';
import Product from '../../Types/Product';

interface WallpaperSimulatorInterface {
  simulatorRooms: SimulatorRoom[];
  selectedProduct?: Product;
}

const WallpaperSimulatorContainer = ({
  simulatorRooms,
  selectedProduct: initialSelectedProduct,
}: WallpaperSimulatorInterface) => {
  const [selectedRoom, setSelectedRoom] = useState<SimulatorRoom>(
    simulatorRooms[0] ?? ({} as SimulatorRoom)
  );

  const [fullScreenModalOpen, setFullScreenModalOpen] =
    useState<boolean>(false);

  const [roomSelectorOpen, setRoomSelectorOpen] = useState<boolean>(true);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const [productSelectorOpen, setProductSelectorOpen] =
    useState<boolean>(true);

  const { products, currentPage, totalProductsCount, pageSize } =
    useTypedSelector((state) => state.productListing);

  const [activeProduct, setActiveProduct] = useState<Product | undefined>(
    initialSelectedProduct
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rooNameFromUrl = urlParams.get('simulatorRoom');
    const roomObject = simulatorRooms.find(
      (r) => r.name === rooNameFromUrl
    );
    if (roomObject !== null && roomObject !== undefined) {
      setSelectedRoom(roomObject);
    }
  }, []);

  const updateUrl = (room, articleNumber) => {
    let hasChanges = false;
    const urlParams = new URLSearchParams(window.location.search);

    if (typeof room?.name !== 'undefined') {
      urlParams.set('simulatorRoom', room.name);
      hasChanges = true;
    }

    if (typeof articleNumber !== 'undefined') {
      urlParams.set('articleNumber', articleNumber);
      hasChanges = true;
    }

    if (hasChanges) {
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState('WallpaperSimulator', '', newUrl);
    }
  };
  useEffect(() => {
    updateUrl(selectedRoom, activeProduct?.id);
    setLinkCopied(false);
  }, [selectedRoom, activeProduct]);

  return (
    <div className="container mb-6 grid grid-cols-1 gap-4 lg:mb-10 lg:grid-cols-2">
      <div className="relative">
        <div className="rounded border border-gray-100 bg-gray-50 pb-2 lg:sticky lg:left-0 lg:right-0 lg:top-0">
          <div className="flex p-2">
            <ChoosenWallpaper
              activeProduct={activeProduct}
              setProductSelectorOpen={setProductSelectorOpen}
            />
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white"
              onClick={() => setFullScreenModalOpen(!fullScreenModalOpen)}
            >
              <Icon name="full-screen" className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center">
            <WallPaperSimulatorCanvas
              simulatorRooms={simulatorRooms}
              selectedRoom={selectedRoom}
              activeProduct={activeProduct}
              setSelectedRoom={setSelectedRoom}
            />
            <RoomPagination
              currentRoomIndex={simulatorRooms.indexOf(selectedRoom)}
              roomAmount={simulatorRooms.length}
              setSelectedRoom={setSelectedRoom}
              simulatorRooms={simulatorRooms}
            />
          </div>
        </div>
      </div>
      <div className="lg:px-8">
        <h1 className="mb-3">{translate('simulator.title')}</h1>
        <WallPaperSimulatorConfigurator
          simulatorRooms={simulatorRooms}
          selectedRoom={selectedRoom}
          setSelectedRoom={setSelectedRoom}
          setSelectedProduct={setActiveProduct}
          products={products}
          roomSelectorOpen={roomSelectorOpen}
          setRoomSelectorOpen={setRoomSelectorOpen}
          productSelectorOpen={productSelectorOpen}
          setProductSelectorOpen={setProductSelectorOpen}
          totalProductsCount={totalProductsCount}
          currentPage={currentPage}
          pageSize={pageSize}
        />
        <WallpaperSimulatorActionButtons
          activeProduct={activeProduct}
          selectedRoom={selectedRoom}
          linkCopied={linkCopied}
          setLinkCopied={setLinkCopied}
        />
      </div>
      <FullScreenDialog
        setFunction={setFullScreenModalOpen}
        isOpen={fullScreenModalOpen}
      >
        <WallPaperSimulatorCanvas
          isFullScreen={true}
          simulatorRooms={simulatorRooms}
          selectedRoom={selectedRoom}
          activeProduct={activeProduct}
          setSelectedRoom={setSelectedRoom}
        />
      </FullScreenDialog>
    </div>
  );
};

export default WallpaperSimulatorContainer;
